import React from "react";
import { Box, HStack, Text, Tooltip } from "@chakra-ui/react";

export const EMOJI_RATING_ITEMS = [
  { value: 1, emoji: "\u{1F621}", label: "Muy malo" },
  { value: 2, emoji: "\u{1F641}", label: "Malo" },
  { value: 3, emoji: "\u{1F610}", label: "Puede mejorar" },
  { value: 4, emoji: "\u{1F60A}", label: "Bueno" },
  { value: 5, emoji: "\u{1F929}", label: "Excelente" },
];

export function ratingItemForValue(value, items = EMOJI_RATING_ITEMS) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return items.find((it) => Number(it.value) === n) ?? null;
}

export function emojiForRating(value, items = EMOJI_RATING_ITEMS) {
  const found = ratingItemForValue(value, items);
  return found?.emoji ?? "\u2014";
}

export function labelForRating(value, items = EMOJI_RATING_ITEMS) {
  const found = ratingItemForValue(value, items);
  return found?.label ?? "Sin calificacion";
}

export function EmojiRatingBar({ value = null, onChange, items = EMOJI_RATING_ITEMS, isDisabled = false, size = "md", ariaLabel = "Calificacion Actividad" }) {
  const [hovered, setHovered] = React.useState(null);
  const buttonRefs = React.useRef([]);
  const normalizedValue = value == null || value === "" ? null : Number(value);
  const fontSize = size === "sm" ? "20px" : size === "lg" ? "30px" : "24px";
  const buttonSize = size === "sm" ? "36px" : size === "lg" ? "52px" : "44px";
  const selectedIndex = items.findIndex((it) => Number(it.value) === normalizedValue);
  const activeIndex = hovered != null ? hovered : selectedIndex;

  const setValueByIndex = React.useCallback((i, shouldFocus = false) => {
    const next = items[i]?.value;
    if (next == null) return;
    onChange?.(Number(next));
    if (shouldFocus) buttonRefs.current[i]?.focus?.();
  }, [items, onChange]);

  const handleArrowKey = React.useCallback((e, baseIndex = 0) => {
    if (isDisabled) return;
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const max = items.length - 1;
    const curr = selectedIndex >= 0 ? selectedIndex : Math.min(Math.max(baseIndex, 0), max);
    const next = e.key === "ArrowLeft" ? Math.max(0, curr - 1) : e.key === "ArrowRight" ? Math.min(max, curr + 1) : e.key === "Home" ? 0 : max;
    setHovered(null);
    setValueByIndex(next, true);
  }, [isDisabled, items.length, selectedIndex, setValueByIndex]);

  return (
    <Box role="radiogroup" aria-label={ariaLabel} onKeyDown={(e) => handleArrowKey(e, selectedIndex >= 0 ? selectedIndex : 0)} bg="dashboard.bg.surfaceMuted" border="0" borderRadius="xl" px="3" py="2" opacity={isDisabled ? 0.6 : 1} pointerEvents={isDisabled ? "none" : "auto"}>
      <HStack gap="2" justify="space-between" onMouseLeave={() => setHovered(null)}>
        {items.map((it, i) => {
          const isActive = i === activeIndex;
          const isChecked = Number(it.value) === normalizedValue;
          return (
            <Tooltip.Root key={it.value} openDelay={0}>
              <Tooltip.Trigger asChild>
                <Box as="button" type="button" role="radio" aria-checked={isChecked} aria-label={it.label} disabled={isDisabled} tabIndex={isChecked || (selectedIndex < 0 && i === 0) ? 0 : -1} ref={(el) => { buttonRefs.current[i] = el; }} onMouseEnter={() => setHovered(i)} onFocus={() => setHovered(i)} onBlur={() => setHovered(null)} onKeyDown={(e) => handleArrowKey(e, i)} onClick={() => onChange?.(Number(it.value))} w={buttonSize} h={buttonSize} borderRadius="xl" display="grid" placeItems="center" transition="background 120ms ease, transform 120ms ease" bg={isActive ? "dashboard.bg.surfaceSubtle" : "transparent"} boxShadow="none" transform={isChecked ? "scale(1.16)" : "scale(1)"} _hover={{ bg: "dashboard.bg.surfaceSubtle", transform: isChecked ? "scale(1.18)" : "translateY(-1px)" }} _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}>
                  <Text fontSize={isChecked ? `calc(${fontSize} + 8px)` : fontSize} lineHeight="1" sx={{ filter: isActive ? "none" : "grayscale(1) saturate(0)", opacity: isActive ? 1 : 0.45, transition: "filter 120ms ease, opacity 120ms ease, font-size 120ms ease", userSelect: "none" }}>
                    {it.emoji}
                  </Text>
                </Box>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>{it.label}</Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          );
        })}
      </HStack>
    </Box>
  );
}
